import type { Analyse, ScoreMarket } from 'r/types';

// Once a market has predictions, Score refuses changes to these fields
// (resending their current values is fine). Texts stay editable.
export const FROZEN_FIELDS = [
  'kolID',
  'symbol',
  'isDirectionUp',
  'tweetID',
  'startDate',
  'entranceDeadline',
  'endDate',
] as const;

export type ScoreMarketBody = {
  analysisId?: string;
  kolID?: string;
  kolUsername?: string;
  kolName?: string;
  kolAvatar?: string | null;
  isDirectionUp?: boolean;
  symbol?: string;
  title?: string | null;
  description?: string | null;
  imageUrl?: string | null;
  tweetID?: string;
  tweetText?: string | null;
  tweetImages?: string[] | null;
  tweetDate?: string | null;
  startDate?: string;
  entranceDeadline?: string;
  endDate?: string;
  deletedAt?: null;
};

export const isMarketStarted = (market: ScoreMarket) =>
  new Date(market.startedAt) <= new Date();

export const getMarketSymbol = (market: ScoreMarket) =>
  `${market.asset}/${market.quoteAsset}`;

export const getMarketTweetID = (market: ScoreMarket) =>
  market.sourceUrl.split('/').pop() ?? '';

export const getMarketPredictions = (market: ScoreMarket) =>
  market.communityStats?.totalPredictions ?? 0;

export const isMarketFrozen = (market: ScoreMarket) =>
  getMarketPredictions(market) > 0;

// Titles name the asset as a cashtag ("Bullish for $HYPE?"). When the pair
// changes, swap the old cashtag for the new one so the title stays right.
export const titleForSymbol = (
  market: ScoreMarket,
  symbol: string,
): ScoreMarketBody => {
  const asset = symbol.split('/')[0];
  if (!market.title || !asset || asset === market.asset) return {};
  const escaped = market.asset.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const cashtag = new RegExp(`\\$${escaped}\\b`, 'gi');
  const title = market.title.replace(cashtag, () => `$${asset}`);
  return title === market.title ? {} : { title };
};

// Score requires a KOL avatar; authors without one get X's default picture
const DEFAULT_KOL_AVATAR =
  'https://abs.twimg.com/sticky/default_profile_images/default_profile_400x400.png';

// The part of a market body that comes from the analysis itself
export const marketBodyFromAnalysis = (analysis: Analyse): ScoreMarketBody => ({
  analysisId: analysis.id,
  kolID: analysis.authorID,
  kolUsername: analysis.authorUsername,
  kolName: analysis.authorName,
  kolAvatar: analysis.authorIcon || DEFAULT_KOL_AVATAR,
  isDirectionUp: analysis.isPredictionUp,
  symbol: analysis.pair,
  tweetID: analysis.tweetID,
  tweetText: analysis.tweetText,
  tweetImages: analysis.images ?? [],
  tweetDate: analysis.tweetDate,
});

// Frozen fields where the analysis and its market disagree
export const getFrozenMismatches = (
  analysis: Analyse,
  market: ScoreMarket,
): string[] => {
  const mismatches: string[] = [];
  if (analysis.authorID !== market.kol.kolID) mismatches.push('KOL');
  if (analysis.pair !== getMarketSymbol(market)) mismatches.push('pair');
  if (analysis.isPredictionUp !== (market.direction === 'up'))
    mismatches.push('direction');
  if (analysis.tweetID !== getMarketTweetID(market)) mismatches.push('tweet');
  return mismatches;
};

// Drops frozen fields from a body when the market has predictions
export const withoutFrozenFields = (
  body: ScoreMarketBody,
  market: ScoreMarket,
): ScoreMarketBody => {
  if (!isMarketFrozen(market)) return body;
  const rest = { ...body };
  FROZEN_FIELDS.forEach((key) => delete rest[key]);
  return rest;
};

// Score errors carry their code in `message` and extras in `details`
export const getScoreError = (e: unknown) => {
  const body = (e as { body?: Record<string, unknown> })?.body;
  const nested = body?.error as { details?: Record<string, unknown> } | null;
  return {
    code: (body?.message as string | undefined) ?? '',
    details: (body?.details ?? nested?.details ?? {}) as Record<
      string,
      unknown
    >,
  };
};
